/**
 * Type Guards for Error Discrimination
 *
 * @see /docs/ERROR_BOUNDARY_IMPROVEMENTS.md for usage patterns
 */
import { ErrorCategory } from '@/types/errors';
import type {
  ApiError,
  AppError,
  ApplicationError,
  AuthError,
  ChunkLoadError,
  MutationError,
  NetworkError,
  QueryError,
  RuntimeError,
  ValidationError,
} from '@/types/errors';

/**
 * Check if error is an ApplicationError
 *
 * Validates that an unknown error has all required ApplicationError properties.
 * Use as the first check in error handling logic.
 *
 * @param error - Any value to check
 * @returns Type predicate narrowing error to ApplicationError
 *
 * @example
 * ```typescript
 * try {
 *   await someOperation();
 * } catch (err) {
 *   if (isApplicationError(err)) {
 *     // TypeScript knows err is ApplicationError here
 *     console.log(err.category, err.severity);
 *   }
 * }
 * ```
 */
export function isApplicationError(error: unknown): error is ApplicationError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'category' in error &&
    'severity' in error &&
    'timestamp' in error &&
    'retryable' in error
  );
}

/**
 * Check if error is a NetworkError
 *
 * @param error - Any value to check
 * @returns Type predicate narrowing error to NetworkError
 *
 * @example
 * ```typescript
 * if (isNetworkError(err)) {
 *   console.log(`${err.method} ${err.url} - ${err.status}`);
 *   if (err.retryable) {
 *     await retryRequest();
 *   }
 * }
 * ```
 */
export function isNetworkError(error: unknown): error is NetworkError {
  return (
    isApplicationError(error) &&
    (error as AppError).category === ErrorCategory.NETWORK
  );
}

/**
 * Check if error is an ApiError
 *
 * ApiError is a NetworkError with a structured backend response.
 *
 * @param error - Any value to check
 * @returns Type predicate narrowing error to ApiError
 *
 * @example
 * ```typescript
 * if (isApiError(err)) {
 *   // Access backend error structure
 *   const { code, translationKey } = err;
 *   toast.error(t(translationKey, err.translationParams));
 * }
 * ```
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    isNetworkError(error) &&
    'response' in error &&
    typeof (error as ApiError).response === 'object' &&
    (error as ApiError).response !== null
  );
}

/**
 * Check if error is a ValidationError
 *
 * @param error - Any value to check
 * @returns Type predicate narrowing error to ValidationError
 *
 * @example
 * ```typescript
 * if (isValidationError(err)) {
 *   // Display field-level errors
 *   err.fields?.email?.forEach(msg => {
 *     setFieldError('email', msg);
 *   });
 * }
 * ```
 */
export function isValidationError(error: unknown): error is ValidationError {
  return (
    isApplicationError(error) &&
    (error as AppError).category === ErrorCategory.VALIDATION
  );
}

/**
 * Check if error is an AuthError (authentication or authorization)
 *
 * @param error - Any value to check
 * @returns Type predicate narrowing error to AuthError
 *
 * @example
 * ```typescript
 * if (isAuthError(err)) {
 *   // Clear auth state and redirect
 *   clearAuth();
 *   if (err.redirectTo) {
 *     router.push(err.redirectTo);
 *   }
 * }
 * ```
 */
export function isAuthError(error: unknown): error is AuthError {
  return (
    isApplicationError(error) &&
    ((error as AppError).category === ErrorCategory.AUTHENTICATION ||
      (error as AppError).category === ErrorCategory.AUTHORIZATION)
  );
}

/**
 * Check if error is an Authentication error specifically
 *
 * @param error - Any value to check
 * @returns Type predicate for authentication errors (login required)
 *
 * @example
 * ```typescript
 * if (isAuthenticationError(err)) {
 *   // User needs to log in
 *   router.push('/login');
 * }
 * ```
 */
export function isAuthenticationError(error: unknown): error is AuthError {
  return (
    isApplicationError(error) &&
    (error as AppError).category === ErrorCategory.AUTHENTICATION
  );
}

/**
 * Check if error is an Authorization error specifically
 *
 * @param error - Any value to check
 * @returns Type predicate for authorization errors (insufficient permissions)
 *
 * @example
 * ```typescript
 * if (isAuthorizationError(err)) {
 *   // User lacks required permissions
 *   toast.error(`You need ${err.requiredPermission} permission`);
 * }
 * ```
 */
export function isAuthorizationError(error: unknown): error is AuthError {
  return (
    isApplicationError(error) &&
    (error as AppError).category === ErrorCategory.AUTHORIZATION
  );
}

/**
 * Check if error is a QueryError (React Query failure)
 *
 * @param error - Any value to check
 * @returns Type predicate for React Query errors
 *
 * @example
 * ```typescript
 * if (isQueryError(err)) {
 *   // React Query specific handling
 *   console.log('Failed query:', err.queryKey);
 *   queryClient.invalidateQueries({ queryKey: err.queryKey });
 * }
 * ```
 */
export function isQueryError(error: unknown): error is QueryError {
  return (
    isApplicationError(error) &&
    'queryKey' in error &&
    Array.isArray((error as QueryError).queryKey)
  );
}

/**
 * Check if error is a MutationError (React Query mutation failure)
 *
 * @param error - Any value to check
 * @returns Type predicate for React Query mutation errors
 *
 * @example
 * ```typescript
 * if (isMutationError(err)) {
 *   console.log('Mutation failed with variables:', err.variables);
 * }
 * ```
 */
export function isMutationError(error: unknown): error is MutationError {
  return isQueryError(error) && 'variables' in error;
}

/**
 * Check if error is a ChunkLoadError
 *
 * @param error - Any value to check
 * @returns Type predicate for lazy-load failures
 *
 * @example
 * ```typescript
 * if (isChunkLoadError(err)) {
 *   // Suggest page refresh
 *   toast.error('Failed to load page. Please refresh.', {
 *     action: { label: 'Refresh', onClick: () => window.location.reload() }
 *   });
 * }
 * ```
 */
export function isChunkLoadError(error: unknown): error is ChunkLoadError {
  return (
    isApplicationError(error) &&
    (error as AppError).category === ErrorCategory.CHUNK_LOAD
  );
}

/**
 * Check if error is a RuntimeError
 *
 * @param error - Any value to check
 * @returns Type predicate for generic runtime errors
 *
 * @example
 * ```typescript
 * if (isRuntimeError(err)) {
 *   // Generic error handling
 *   console.error('Runtime error:', err.message);
 *   logError(err, { componentStack: err.componentStack });
 * }
 * ```
 */
export function isRuntimeError(error: unknown): error is RuntimeError {
  return (
    isApplicationError(error) &&
    (error as AppError).category === ErrorCategory.RUNTIME
  );
}

/**
 * Check if error is retryable
 */
export function isRetryable(error: unknown): boolean {
  if (!isApplicationError(error)) {
    return false;
  }
  return error.retryable === true;
}

/**
 * Get error category
 */
export function getErrorCategory(error: unknown): ErrorCategory | null {
  if (!isApplicationError(error)) {
    return null;
  }
  return error.category;
}

/**
 * Check if error should be logged
 */
export function shouldLog(error: unknown): boolean {
  // Don't log null or undefined
  if (error === null || error === undefined) {
    return false;
  }

  // Always log ApplicationErrors
  if (isApplicationError(error)) {
    return true;
  }

  // Log all Error instances
  if (error instanceof Error) {
    return true;
  }

  // Log Error-like objects with message property
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return true;
  }

  // Log string errors (can be converted to error messages)
  if (typeof error === 'string' && error.length > 0) {
    return true;
  }

  // Don't log other primitive values (numbers, booleans, etc.)
  return false;
}
